import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Camera, CameraType } from '../Camera';
import Header from '../components/Header'; // Import the Header component
import axios from 'axios';
import { useAppContext } from "../AppContext";
import { useNavigate } from "react-router";

const pica = require("pica")();
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: calc(100vh - 60px); /* Adjust for header height */
  background-color: #f0f0f0;
  padding: 20px;
  box-sizing: border-box;
`;

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
  text-align: center;
`;

const CameraWrapper = styled.div`
  width: 80%;
  max-width: 400px;
  height: 300px;
  background-color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 2px solid #ccc;
  margin-bottom: 20px;
`;

const TakePhotoButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  border: none;

  &:hover {
    background-color: #0056b3;
  }
`;

const ConfirmationButtons = styled.div`
  display: flex;
  justify-content: space-around;
  width: 80%;
  max-width: 400px;
  margin-top: 20px;
`;

const ConfirmationButton = styled.button<{ color: string }>`
  background-color: ${(props) => props.color};
  color: ${(props) => (props.color === 'lightgray' ? '#333' : 'white')};
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  border: ${(props) => (props.color === 'lightgray' ? 'none' : '1px solid transparent')};
  width: 200px;
  margin: 0 10px;

  &:hover {
    background-color: ${(props) => (props.color === 'lightgray' ? '#bbb' : '#0056b3')};
  }
`;

const CameraFeed = styled(Camera)`
  width: 100%;
  height: 100%;
`;

const IneAtras: React.FC<{ onCapture: (photo: string) => void;  }> = ({ onCapture }) => {
  const camera = useRef<CameraType>(null);
  const [image, setImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<string | null>(null);
  const alldata = useAppContext();
  const navigate = useNavigate();

  const handleTakePhoto = async () => {
    if (camera.current) {
      const photo = camera.current.takePhoto();
      setImage(photo as string);
    }
  };

  const handleConfirm = () => {
    if (image) {
      onCapture(image);
    }
  };

  const handleRetake = () => {
    setImage(null);
    setSelectedFile(null);
    setUploadResult(null);
  };

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
     // setImage(null); // Optional: clear camera image if uploading file
    }
  };

  // Upload selected file to server
  const handleUploadFile = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadResult(null);

    const reader = new FileReader();
    reader.readAsDataURL(selectedFile);

    reader.onload = async (e) => {
      try {
        // --- Resize logic ---
        // If you have alldata context, log it
        const photo = e.target?.result as string;
        const width = 1920;
        const height = 1080;
        const htmlimage = new window.Image(width, height);
        htmlimage.src = photo;

        // Wait for image to load
        await new Promise((resolve, reject) => {
          htmlimage.onload = resolve;
          htmlimage.onerror = reject;
        });

        const outputCanvas = document.createElement("canvas");
        outputCanvas.width = 1280;
        outputCanvas.height = 720;

        try {
          await pica.resize(htmlimage, outputCanvas);
        } catch (error) {
          console.error("Error resizing image:", error);
          setUploadResult('Error al redimensionar la imagen');
          setUploading(false);
          return;
        }

        const base64String = outputCanvas.toDataURL();

        // --- Upload logic with alldata and navigate ---
        try {
          await axios
            .post("https://gruporomeroseguros.com/photo", { photo: base64String })
            .then(async (response) => {
              console.log("Response Data:", response.data);
              alldata.setIneAtrasPhoto(base64String);
              alldata.setIneAtrasPhotoNombre(response.data.toString());
              setUploadResult('Archivo enviado correctamente');
            })
            .finally(() => {
              navigate("/InputInformation/");
            });
        } catch (error) {
          setUploadResult('Error al enviar el archivo');
          console.error("Error uploading photo:", error);
        }
      } catch (error) {
        setUploadResult('Error al procesar la imagen');
        console.error(error);
      }
      setUploading(false);
    };

    reader.onerror = () => {
      setUploadResult('Error al leer el archivo');
      setUploading(false);
    };
  };

  return (
    <>
      {/* Add the Header component with a back button */}
      <Header title="INE o Licencia Atras" />
      <Wrapper>
        <Title>Por favor toma una fotografía de la parte trasera de tu INE o Licencia</Title>
        {image ? (
          <>
            <img src={image} alt="INE o Licencia Atras" style={{ width: '80%', maxWidth: '400px', marginBottom: '20px' }} />
            <ConfirmationButtons>
              <ConfirmationButton color="lightgray" onClick={handleRetake}>Volver a Intentar</ConfirmationButton>
              <ConfirmationButton color="green" onClick={handleConfirm}>Sí</ConfirmationButton>
            </ConfirmationButtons>
          </>
        ) : (
          <>
            <CameraWrapper>
              <CameraFeed
                ref={camera}
                aspectRatio="cover"
                facingMode="environment"
                numberOfCamerasCallback={(i: number) => console.log(i)}
                errorMessages={{
                  noCameraAccessible:
                    'No camera device accessible. Please connect your camera or try a different browser.',
                  permissionDenied: 'Permission denied. Please refresh and give camera permission.',
                  switchCamera:
                    'It is not possible to switch camera to different one because there is only one video device accessible.',
                  canvas: 'Canvas is not supported.',
                }}
                videoReadyCallback={() => {
                  console.log('Video feed ready.');
                }}
              />
            </CameraWrapper>
            <TakePhotoButton onClick={handleTakePhoto}>Tomar fotografía</TakePhotoButton>
          </>
        )}

        {/* Divider */}
        <div style={{ margin: '20px 0', fontWeight: 'bold' }}>O selecciona una foto de tu dispositivo</div>

        {/* File input and upload button */}
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ marginBottom: 10 }}
        />
        <TakePhotoButton onClick={handleUploadFile} disabled={!selectedFile || uploading}>
          {uploading ? 'Enviando...' : 'Enviar archivo'}
        </TakePhotoButton>
        {uploadResult && <div style={{ marginTop: 10 }}>{uploadResult}</div>}
      </Wrapper>
    </>
  );
};

export default IneAtras;
